import React, { useEffect, useMemo, useState } from "react";

import HttpManager from "../../../services/HttpManager";
import { WaChannel, WaState, WaTemplate } from "../../../services/v2/prospects.service";
import { errorTexto, faltan } from "./salesHelpers";

interface Props {
	campusId: number;
	prospectId: number;
	firstName: string;
	countryId: number | null;
	wa: { state: WaState; channels: WaChannel[] } | null;
	texto: string;                              // lo controla la ficha: "Enviar al chat" de Horarios escribe aquí
	setTexto: (t: string) => void;
	ubicacion: string | null;
	onEnviado: () => void;
	onCanal: (channelId: number) => void;
}

/**
 * Abajo del chat (lienzo): Mensaje sale por WhatsApp; Nota se queda en WAPP. Dentro de las 24 h desde el último
 * mensaje de la persona se escribe libre; fuera, Meta solo deja mandar una plantilla aprobada.
 */
const Compositor: React.FC<Props> = ({ campusId, prospectId, firstName, countryId, wa, texto, setTexto, ubicacion, onEnviado, onCanal }) => {
	const http = HttpManager.getInstance();
	const [modo, setModo] = useState<"mensaje" | "nota">("mensaje");
	const [plantillas, setPlantillas] = useState<WaTemplate[] | null>(null);
	const [usarPlantilla, setUsarPlantilla] = useState<boolean>(false);
	const [tpl, setTpl] = useState<string>("");
	const [params, setParams] = useState<string[]>([]);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	const state = wa?.state;
	const canalId = state?.channelId ?? null;
	const abierta = !!state?.windowOpen;
	const conPlantilla = modo === "mensaje" && (usarPlantilla || !abierta);
	const canales = useMemo(() => (wa?.channels || []).filter((c) => !countryId || c.country_id === countryId || c.id === canalId), [wa, countryId, canalId]);

	useEffect(() => { setUsarPlantilla(false); setTpl(""); setParams([]); setError(null); setModo("mensaje"); }, [prospectId]);
	useEffect(() => {
		setPlantillas(null);
		if (!canalId || !conPlantilla) return;
		http.prospectsService.waTemplates(campusId, canalId).then((r) => setPlantillas(r.data.templates)).catch((e) => setError(errorTexto(e)));
	}, [canalId, conPlantilla]);

	const plantilla = plantillas?.find((t) => t.name + "|" + t.language === tpl) || null;
	useEffect(() => {
		if (!plantilla) return;
		setParams(Array.from({ length: plantilla.params }, (_, i) => (i === 0 ? firstName : "")));
	}, [tpl]);
	const vista = plantilla ? plantilla.body.replace(/\{\{(\d+)\}\}/g, (m, n) => params[Number(n) - 1] || m) : "";

	const enviar = async () => {
		setBusy(true); setError(null);
		try {
			if (modo === "nota") {
				await http.prospectsService.note(campusId, prospectId, texto.trim(), "nota");
				setTexto("");
			} else if (conPlantilla) {
				if (!plantilla) throw new Error("Elige una plantilla.");
				await http.prospectsService.sendWhatsapp(campusId, prospectId, { template: plantilla.name, language: plantilla.language, params, channelId: canalId });
				setTpl(""); setUsarPlantilla(false);
			} else {
				await http.prospectsService.sendWhatsapp(campusId, prospectId, { text: texto.trim(), channelId: canalId });
				setTexto("");
			}
			onEnviado();
		} catch (e) { setError(errorTexto(e)); }
		finally { setBusy(false); }
	};

	const puede = !busy && (modo === "nota" ? !!texto.trim()
		: !!state?.canSend && (conPlantilla ? !!plantilla && params.every((p) => p.trim()) : !!texto.trim()));

	return (
		<div className="cv-compositor">
			{modo === "mensaje" && state ? (
				<div className={"cv-ventana" + (abierta ? " abierta" : "")}>
					{state.optOut ? "Pidió no recibir WhatsApp"
						: !canalId ? "Sin número de WhatsApp para su plantel"
						: abierta ? "Ventana abierta · " + faltan(state.windowClosesAt)
						: state.lastInboundAt ? "Pasaron 24 h desde su último mensaje: solo plantilla" : "Nunca nos ha escrito: solo plantilla"}
					{canales.length > 1 ? (
						<select value={canalId || ""} onChange={(e) => onCanal(Number(e.target.value))} aria-label="Desde qué número">
							{canales.map((c) => <option key={c.id} value={c.id}>{(c.label || c.name || "") + " · " + c.display_phone}</option>)}
						</select>
					) : canalId ? <span className="cv-desde">desde {canales.find((c) => c.id === canalId)?.label || ""}</span> : null}
				</div>
			) : null}

			<div className="cv-rapidos">
				<div className="cv-modo" role="tablist" aria-label="Tipo">
					<button role="tab" aria-selected={modo === "mensaje"} className={modo === "mensaje" ? "on" : ""} onClick={() => setModo("mensaje")}>Mensaje</button>
					<button role="tab" aria-selected={modo === "nota"} className={modo === "nota" ? "on" : ""} onClick={() => setModo("nota")}>Nota</button>
				</div>
				{modo === "mensaje" && abierta ? (
					<>
						{ubicacion ? <button className="btn-wa" onClick={() => setTexto((texto ? texto + "\n\n" : "") + ubicacion)}>Ubicación</button> : null}
						<button className={"btn-wa" + (usarPlantilla ? " pri" : "")} onClick={() => setUsarPlantilla(!usarPlantilla)}>Plantilla</button>
					</>
				) : null}
			</div>

			{conPlantilla ? (
				<div className="cv-plantillas">
					<select value={tpl} onChange={(e) => setTpl(e.target.value)} aria-label="Plantilla" disabled={!plantillas}>
						<option value="">{plantillas ? "Elegir plantilla (" + plantillas.length + ")" : "Cargando plantillas…"}</option>
						{(plantillas || []).map((t) => <option key={t.name + t.language} value={t.name + "|" + t.language}>{t.name}{t.category === "MARKETING" ? " · promoción" : ""}</option>)}
					</select>
					{plantilla ? (
						<>
							{params.map((v, i) => (
								<input key={i} value={v} placeholder={"Dato " + (i + 1)} aria-label={"Dato " + (i + 1)}
									onChange={(e) => setParams(params.map((x, j) => (j === i ? e.target.value : x)))} />
							))}
							<p className="cv-vista">{vista}</p>
						</>
					) : null}
				</div>
			) : null}

			<div className="cv-escribir">
				{!conPlantilla ? (
					<textarea rows={2} value={texto} onChange={(e) => setTexto(e.target.value)} aria-label={modo === "nota" ? "Nota" : "Mensaje"}
						placeholder={modo === "nota" ? "Nota interna (no le llega al cliente)" : "Escribir mensaje"}
						onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && puede) enviar(); }} />
				) : <div style={{ flex: 1 }} />}
				<button className="cv-enviar" disabled={!puede} onClick={enviar}>{busy ? "…" : modo === "nota" ? "Guardar" : conPlantilla ? "Enviar plantilla" : "Enviar"}</button>
			</div>
			{error ? <div className="aviso error">{error}</div> : null}
		</div>
	);
};

export default Compositor;
